<template>
  <div class="finance-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.finance.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.finance.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button v-if="activeTab === 'transactions'" class="btn btn-secondary" :disabled="exportingCsv" @click="handleExportCsv">
          <span v-if="exportingCsv" class="spinner spinner-sm"></span>
          {{ $t('admin.finance.export_csv') }}
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        @click="switchTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- ==================== TRANSACTIONS TAB ==================== -->
    <div v-if="activeTab === 'transactions'">
      <!-- Filters -->
      <div class="filters-row">
        <select v-model="txFilters.type" class="filter-select" @change="fetchTransactions(1)">
          <option value="">{{ $t('admin.finance.filter_all_types') }}</option>
          <option value="deposit">{{ $t('admin.finance.filter_deposit') }}</option>
          <option value="withdrawal">{{ $t('admin.finance.filter_withdrawal') }}</option>
          <option value="bet">{{ $t('admin.finance.filter_bet') }}</option>
          <option value="win">{{ $t('admin.finance.filter_win') }}</option>
          <option value="bonus">{{ $t('admin.finance.filter_bonus') }}</option>
          <option value="adjustment">{{ $t('admin.finance.filter_adjustment') }}</option>
        </select>

        <select v-model="txFilters.status" class="filter-select" @change="fetchTransactions(1)">
          <option value="">{{ $t('admin.finance.filter_all_statuses') }}</option>
          <option value="completed">{{ $t('admin.finance.filter_completed') }}</option>
          <option value="pending">{{ $t('admin.finance.filter_pending') }}</option>
          <option value="failed">{{ $t('admin.finance.filter_failed') }}</option>
          <option value="cancelled">{{ $t('admin.finance.filter_cancelled') }}</option>
        </select>

        <input
          v-model="txFilters.date_from"
          type="date"
          class="filter-input"
          @change="fetchTransactions(1)"
        >
        <input
          v-model="txFilters.date_to"
          type="date"
          class="filter-input"
          @change="fetchTransactions(1)"
        >

        <input
          v-model="txFilters.amount_min"
          type="number"
          class="filter-input filter-amount"
          :placeholder="$t('admin.finance.filter_min_amount')"
          @change="fetchTransactions(1)"
        >
        <input
          v-model="txFilters.amount_max"
          type="number"
          class="filter-input filter-amount"
          :placeholder="$t('admin.finance.filter_max_amount')"
          @change="fetchTransactions(1)"
        >

        <div class="search-wrap">
          <input
            v-model="txFilters.player_search"
            type="text"
            class="filter-input search-input"
            :placeholder="$t('admin.finance.filter_search_player')"
            @keyup.enter="fetchTransactions(1)"
          >
          <button class="btn-search" @click="fetchTransactions(1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Transactions Table -->
      <div class="table-container">
        <div v-if="txLoading" class="table-loading">
          <span class="spinner"></span>
          {{ $t('admin.finance.loading_transactions') }}
        </div>

        <template v-else>
          <table>
            <thead>
              <tr>
                <th>{{ $t('admin.finance.col_player') }}</th>
                <th>{{ $t('admin.finance.col_type') }}</th>
                <th class="th-sortable" @click="toggleTxSort('amount')">
                  {{ $t('admin.finance.col_amount') }}
                  <span v-if="txSort.field === 'amount'" class="sort-indicator">{{ txSort.direction === 'asc' ? '↑' : '↓' }}</span>
                </th>
                <th>{{ $t('admin.finance.col_currency') }}</th>
                <th>{{ $t('admin.finance.col_status') }}</th>
                <th>{{ $t('admin.finance.col_method') }}</th>
                <th class="th-sortable" @click="toggleTxSort('created_at')">
                  {{ $t('admin.finance.col_date') }}
                  <span v-if="txSort.field === 'created_at'" class="sort-indicator">{{ txSort.direction === 'asc' ? '↑' : '↓' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="transactions.length === 0">
                <td colspan="7" class="empty-row">{{ $t('admin.finance.no_transactions') }}</td>
              </tr>
              <tr v-for="tx in transactions" :key="tx.id">
                <td>
                  <div class="player-cell">
                    <span class="player-username">{{ tx.user.username }}</span>
                    <span class="player-email">{{ tx.user.email }}</span>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="'badge-type-' + tx.type">{{ tx.type }}</span>
                </td>
                <td>
                  <span class="amount-value" :class="tx.amount >= 0 ? 'amount-positive' : 'amount-negative'">
                    {{ formatCents(tx.amount) }}
                  </span>
                </td>
                <td>{{ tx.currency }}</td>
                <td>
                  <span class="badge" :class="'badge-' + tx.status">{{ tx.status }}</span>
                </td>
                <td>
                  <span v-if="tx.payment_method" class="method-badge">{{ formatMethod(tx.payment_method) }}</span>
                  <span v-else class="text-muted">&mdash;</span>
                </td>
                <td class="td-date">{{ formatDate(tx.created_at) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div v-if="txMeta.last_page > 1" class="pagination">
            <button class="pagination-btn" :disabled="txMeta.current_page <= 1" @click="fetchTransactions(txMeta.current_page - 1)">&larr;</button>
            <button
              v-for="page in buildPageRange(txMeta.current_page, txMeta.last_page)"
              :key="page"
              class="pagination-btn"
              :class="{ active: page === txMeta.current_page }"
              @click="fetchTransactions(page)"
            >
              {{ page }}
            </button>
            <span class="pagination-info">
              {{ (txMeta.current_page - 1) * txMeta.per_page + 1 }}-{{ Math.min(txMeta.current_page * txMeta.per_page, txMeta.total) }} of {{ txMeta.total }}
            </span>
            <button class="pagination-btn" :disabled="txMeta.current_page >= txMeta.last_page" @click="fetchTransactions(txMeta.current_page + 1)">&rarr;</button>
          </div>
        </template>
      </div>
    </div>

    <!-- ==================== WITHDRAWALS TAB ==================== -->
    <div v-if="activeTab === 'withdrawals'">
      <!-- Section Header -->
      <div class="section-header">
        <h2 class="section-title">{{ $t('admin.finance.withdrawal_queue') }}</h2>
        <div class="section-actions">
          <button
            v-if="selectedIds.length > 0"
            class="btn btn-primary"
            :disabled="batchApproving"
            @click="handleBatchApprove"
          >
            <span v-if="batchApproving" class="spinner spinner-sm"></span>
            {{ $t('admin.finance.approve_selected', { n: selectedIds.length }) }}
          </button>
        </div>
      </div>

      <!-- Withdrawal Filters -->
      <div class="filters-row">
        <select v-model="wdFilters.status" class="filter-select" @change="fetchWithdrawals(1)">
          <option value="">{{ $t('admin.finance.wd_filter_all_statuses') }}</option>
          <option value="pending">{{ $t('admin.finance.wd_filter_pending') }}</option>
          <option value="completed">{{ $t('admin.finance.wd_filter_completed') }}</option>
          <option value="failed">{{ $t('admin.finance.wd_filter_failed') }}</option>
          <option value="cancelled">{{ $t('admin.finance.wd_filter_cancelled') }}</option>
        </select>

        <input
          v-model="wdFilters.date_from"
          type="date"
          class="filter-input"
          @change="fetchWithdrawals(1)"
        >
        <input
          v-model="wdFilters.date_to"
          type="date"
          class="filter-input"
          @change="fetchWithdrawals(1)"
        >

        <div class="search-wrap">
          <input
            v-model="wdFilters.search"
            type="text"
            class="filter-input search-input"
            :placeholder="$t('admin.finance.wd_search_player')"
            @keyup.enter="fetchWithdrawals(1)"
          >
          <button class="btn-search" @click="fetchWithdrawals(1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Withdrawals Table -->
      <div class="table-container">
        <div v-if="wdLoading" class="table-loading">
          <span class="spinner"></span>
          {{ $t('admin.finance.loading_withdrawals') }}
        </div>

        <template v-else>
          <table>
            <thead>
              <tr>
                <th class="th-checkbox">
                  <input
                    type="checkbox"
                    :checked="allPendingSelected"
                    :indeterminate="somePendingSelected && !allPendingSelected"
                    @change="toggleSelectAll"
                  >
                </th>
                <th>{{ $t('admin.finance.col_wd_player') }}</th>
                <th>{{ $t('admin.finance.col_wd_amount') }}</th>
                <th>{{ $t('admin.finance.col_wd_method') }}</th>
                <th>{{ $t('admin.finance.col_wd_address') }}</th>
                <th>{{ $t('admin.finance.col_wd_status') }}</th>
                <th>{{ $t('admin.finance.col_wd_date') }}</th>
                <th>{{ $t('admin.finance.col_wd_date') }}</th>
                <th>{{ $t('admin.finance.col_wd_actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="withdrawals.length === 0">
                <td colspan="9" class="empty-row">{{ $t('admin.finance.no_withdrawals') }}</td>
              </tr>
              <template v-for="tx in withdrawals" :key="tx.id">
                <tr
                  class="withdrawal-row"
                  :class="{ expanded: expandedId === tx.id }"
                  @click="toggleExpand(tx.id)"
                >
                  <td class="td-checkbox" @click.stop>
                    <input
                      v-if="tx.status === 'pending'"
                      type="checkbox"
                      :checked="selectedIds.includes(tx.id)"
                      @change="toggleSelect(tx.id)"
                    >
                  </td>
                  <td>
                    <div class="player-cell">
                      <span class="player-username">{{ tx.player_username }}</span>
                      <span class="player-email">{{ tx.player_email }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="amount-value amount-negative">{{ formatDecimalAmount(tx.amount) }}</span>
                    <span class="amount-currency">{{ tx.currency }}</span>
                  </td>
                  <td>
                    <span class="method-badge">{{ formatMethod(tx.payment_method) }}</span>
                  </td>
                  <td>
                    <span class="destination-text" :title="tx.destination_address ?? undefined">
                      {{ truncateAddress(tx.destination_address) }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="'badge-' + tx.status">{{ tx.status }}</span>
                  </td>
                  <td class="td-date">{{ formatDate(tx.created_at) }}</td>
                  <td class="td-date">{{ tx.processed_at ? formatDate(tx.processed_at) : '—' }}</td>
                  <td @click.stop>
                    <div v-if="tx.status === 'pending'" class="action-buttons">
                      <button
                        class="btn-action btn-approve"
                        title="Approve"
                        :disabled="approvingId === tx.id"
                        @click="handleApprove(tx.id)"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                      <button
                        class="btn-action btn-reject"
                        title="Reject"
                        @click="openRejectModal(tx)"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                    <span v-else class="text-muted">&mdash;</span>
                  </td>
                </tr>
                <!-- Expanded player context -->
                <tr v-if="expandedId === tx.id" class="expanded-row">
                  <td colspan="9">
                    <div v-if="expandedLoading" class="expanded-loading">
                      <span class="spinner spinner-sm"></span>
                      Loading player details...
                    </div>
                    <div v-else-if="expandedDetail" class="expanded-content">
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="detail-label">Balance</span>
                          <span class="detail-value">{{ formatDecimal(expandedDetail.player?.balance) }} {{ expandedDetail.currency }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Total Deposited</span>
                          <span class="detail-value">{{ formatDecimal(expandedDetail.player?.total_deposited) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Total Withdrawn</span>
                          <span class="detail-value">{{ formatDecimal(expandedDetail.player?.total_withdrawn) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">KYC Status</span>
                          <span class="badge" :class="'badge-kyc-' + (expandedDetail.player?.kyc_status || 'none')">
                            {{ expandedDetail.player?.kyc_status || 'none' }}
                          </span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">VIP Level</span>
                          <span class="detail-value">{{ expandedDetail.player?.vip_level ?? 0 }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Account Status</span>
                          <span class="detail-value">{{ expandedDetail.player?.status || 'active' }}</span>
                        </div>
                      </div>
                      <div v-if="expandedDetail.destination_address" class="detail-full-address">
                        <span class="detail-label">Full Address:</span>
                        <code>{{ expandedDetail.destination_address }}</code>
                      </div>
                      <div v-if="expandedDetail.notes" class="detail-notes">
                        <span class="detail-label">Notes:</span>
                        <span>{{ expandedDetail.notes }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- Pagination -->
          <div v-if="wdMeta.last_page > 1" class="pagination">
            <button class="pagination-btn" :disabled="wdMeta.current_page <= 1" @click="fetchWithdrawals(wdMeta.current_page - 1)">&larr;</button>
            <button
              v-for="page in buildPageRange(wdMeta.current_page, wdMeta.last_page)"
              :key="page"
              class="pagination-btn"
              :class="{ active: page === wdMeta.current_page }"
              @click="fetchWithdrawals(page)"
            >
              {{ page }}
            </button>
            <span class="pagination-info">
              {{ (wdMeta.current_page - 1) * wdMeta.per_page + 1 }}-{{ Math.min(wdMeta.current_page * wdMeta.per_page, wdMeta.total) }} of {{ wdMeta.total }}
            </span>
            <button class="pagination-btn" :disabled="wdMeta.current_page >= wdMeta.last_page" @click="fetchWithdrawals(wdMeta.current_page + 1)">&rarr;</button>
          </div>
        </template>
      </div>
    </div>

    <!-- ==================== REPORTS TAB ==================== -->
    <div v-if="activeTab === 'reports'">
      <!-- Period Selector -->
      <div class="filters-row">
        <select v-model="reportPeriod" class="filter-select" @change="onReportPeriodChange">
          <option value="today">{{ $t('admin.finance.period_today') }}</option>
          <option value="week">{{ $t('admin.finance.period_week') }}</option>
          <option value="month">{{ $t('admin.finance.period_month') }}</option>
          <option value="custom">{{ $t('admin.finance.period_custom') }}</option>
        </select>

        <template v-if="reportPeriod === 'custom'">
          <input
            v-model="reportDateFrom"
            type="date"
            class="filter-input"
            @change="fetchSummary"
          >
          <input
            v-model="reportDateTo"
            type="date"
            class="filter-input"
            @change="fetchSummary"
          >
        </template>
      </div>

      <!-- Summary Cards -->
      <div v-if="reportLoading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.finance.loading_summary') }}
      </div>

      <template v-else>
        <div class="stat-grid">
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_total_deposits') }}</div>
            <div class="stat-value stat-positive">{{ formatCents(summary.total_deposits) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_total_withdrawals') }}</div>
            <div class="stat-value stat-negative">{{ formatCents(summary.total_withdrawals) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_ggr') }}</div>
            <div class="stat-value" :class="summary.ggr >= 0 ? 'stat-positive' : 'stat-negative'">{{ formatCents(summary.ggr) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_ngr') }}</div>
            <div class="stat-value" :class="summary.ngr >= 0 ? 'stat-positive' : 'stat-negative'">{{ formatCents(summary.ngr) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_total_bets') }}</div>
            <div class="stat-value">{{ formatCents(summary.total_bets) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_total_wins') }}</div>
            <div class="stat-value">{{ formatCents(summary.total_wins) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ $t('admin.finance.stat_bonus_cost') }}</div>
            <div class="stat-value stat-negative">{{ formatCents(summary.bonus_cost) }}</div>
          </div>
        </div>

        <!-- Currency Breakdown -->
        <div v-if="summary.by_currency.length > 0" class="breakdown-section">
          <h3 class="breakdown-title">{{ $t('admin.finance.breakdown_currency') }}</h3>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>{{ $t('admin.finance.col_currency') }}</th>
                  <th>{{ $t('admin.finance.col_deposits') }}</th>
                  <th>{{ $t('admin.finance.col_withdrawals') }}</th>
                  <th>{{ $t('admin.finance.col_net') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.by_currency" :key="row.currency">
                  <td><span class="currency-label">{{ row.currency }}</span></td>
                  <td><span class="amount-positive">{{ formatCents(row.deposits) }}</span></td>
                  <td><span class="amount-negative">{{ formatCents(row.withdrawals) }}</span></td>
                  <td>
                    <span :class="row.deposits - row.withdrawals >= 0 ? 'amount-positive' : 'amount-negative'">
                      {{ formatCents(row.deposits - row.withdrawals) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Payment Method Breakdown -->
        <div v-if="summary.by_payment_method.length > 0" class="breakdown-section">
          <h3 class="breakdown-title">{{ $t('admin.finance.breakdown_payment_method') }}</h3>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>{{ $t('admin.finance.col_method') }}</th>
                  <th>{{ $t('admin.finance.col_deposits') }}</th>
                  <th>{{ $t('admin.finance.col_withdrawals') }}</th>
                  <th>{{ $t('admin.finance.col_transactions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.by_payment_method" :key="row.payment_method">
                  <td><span class="method-badge">{{ formatMethod(row.payment_method) }}</span></td>
                  <td><span class="amount-positive">{{ formatCents(row.deposits) }}</span></td>
                  <td><span class="amount-negative">{{ formatCents(row.withdrawals) }}</span></td>
                  <td>{{ row.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>

    <!-- Reject Modal -->
    <div v-if="rejectModal.visible" class="modal-overlay" @click.self="closeRejectModal">
      <div class="modal-content">
        <h3 class="modal-title">{{ $t('admin.finance.reject_modal_title') }}</h3>
        <p class="modal-subtitle">
          {{ $t('admin.finance.reject_modal_subtitle', { amount: formatDecimalAmount(rejectModal.amount), currency: rejectModal.currency, username: rejectModal.username }) }}
        </p>
        <div class="modal-field">
          <label class="modal-label" for="reject-reason">{{ $t('admin.finance.reject_reason_label') }}</label>
          <textarea
            id="reject-reason"
            v-model="rejectModal.reason"
            class="modal-textarea"
            rows="3"
            :placeholder="$t('admin.finance.reject_reason_placeholder')"
          ></textarea>
        </div>
        <div v-if="rejectModal.error" class="modal-error">{{ rejectModal.error }}</div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeRejectModal">{{ $t('admin.finance.cancel') }}</button>
          <button
            class="btn btn-danger"
            :disabled="!rejectModal.reason.trim() || rejectModal.loading"
            @click="handleReject"
          >
            <span v-if="rejectModal.loading" class="spinner spinner-sm"></span>
            {{ $t('admin.finance.reject_and_refund') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

// ==================== Types ====================

interface Transaction {
  id: number
  user: { username: string; email: string }
  type: string
  amount: number
  currency: string
  status: string
  payment_method: string | null
  created_at: string
}

interface Withdrawal {
  id: number
  player_username: string
  player_email: string
  player_kyc_status: string | null
  amount: string
  currency: string
  payment_method: string
  destination_address: string | null
  status: string
  created_at: string
  processed_at: string | null
}

interface WithdrawalDetail {
  id: number
  amount: string
  currency: string
  payment_method: string
  destination_address: string | null
  status: string
  notes: string | null
  created_at: string
  processed_at: string | null
  processed_by: { id: number; name: string } | null
  player: {
    id: number
    username: string
    email: string
    status: string
    kyc_status: string
    vip_level: number
    balance: string
    total_deposited: string
    total_withdrawn: string
  } | null
}

interface CurrencyBreakdown {
  currency: string
  deposits: number
  withdrawals: number
}

interface PaymentMethodBreakdown {
  payment_method: string
  deposits: number
  withdrawals: number
  count: number
}

interface FinanceSummary {
  total_deposits: number
  total_withdrawals: number
  total_bets: number
  total_wins: number
  ggr: number
  ngr: number
  bonus_cost: number
  by_currency: CurrencyBreakdown[]
  by_payment_method: PaymentMethodBreakdown[]
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ==================== State ====================

const { t } = useI18n()
const toast = useToastStore()

const tabs = computed(() => [
  { id: 'transactions', label: t('admin.finance.tab_transactions') },
  { id: 'withdrawals', label: t('admin.finance.tab_withdrawals') },
  { id: 'reports', label: t('admin.finance.tab_reports') },
])

const activeTab = ref('transactions')
const tabLoaded = ref<Record<string, boolean>>({ transactions: false, withdrawals: false, reports: false })

// --- Transactions ---
const transactions = ref<Transaction[]>([])
const txLoading = ref(false)
const txMeta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 25, total: 0 })
const txFilters = ref({
  type: '',
  status: '',
  date_from: '',
  date_to: '',
  player_search: '',
  amount_min: '',
  amount_max: '',
})
const txSort = ref({ field: 'created_at', direction: 'desc' })
const exportingCsv = ref(false)

// --- Withdrawals ---
const withdrawals = ref<Withdrawal[]>([])
const wdLoading = ref(false)
const wdMeta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })
const wdFilters = ref({ status: '', date_from: '', date_to: '', search: '' })

const selectedIds = ref<number[]>([])
const approvingId = ref<number | null>(null)
const batchApproving = ref(false)

const expandedId = ref<number | null>(null)
const expandedDetail = ref<WithdrawalDetail | null>(null)
const expandedLoading = ref(false)

const rejectModal = ref({
  visible: false,
  transactionId: 0,
  username: '',
  amount: '',
  currency: '',
  reason: '',
  loading: false,
  error: '',
})

// --- Reports ---
const reportLoading = ref(false)
const reportPeriod = ref('month')
const reportDateFrom = ref('')
const reportDateTo = ref('')
const summary = ref<FinanceSummary>({
  total_deposits: 0,
  total_withdrawals: 0,
  total_bets: 0,
  total_wins: 0,
  ggr: 0,
  ngr: 0,
  bonus_cost: 0,
  by_currency: [],
  by_payment_method: [],
})

// ==================== Computed ====================

const pendingIds = computed(() =>
  withdrawals.value.filter((w) => w.status === 'pending').map((w) => w.id),
)

const allPendingSelected = computed(
  () => pendingIds.value.length > 0 && pendingIds.value.every((id) => selectedIds.value.includes(id)),
)

const somePendingSelected = computed(
  () => pendingIds.value.some((id) => selectedIds.value.includes(id)),
)

// ==================== Formatting Helpers ====================

function formatCents(cents: number): string {
  const dollars = cents / 100
  const sign = dollars < 0 ? '-' : ''
  return sign + '$' + Math.abs(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDecimalAmount(val: string | undefined): string {
  if (!val) return '$0.00'
  const num = parseFloat(val)
  return '$' + Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDecimal(val: string | undefined): string {
  if (!val) return '0.00'
  const num = parseFloat(val)
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatMethod(method: string | null): string {
  if (!method) return '—'
  return method.charAt(0).toUpperCase() + method.slice(1)
}

function truncateAddress(addr: string | null): string {
  if (!addr) return '—'
  if (addr.length <= 16) return addr
  return addr.slice(0, 8) + '...' + addr.slice(-6)
}

function buildPageRange(current: number, total: number): number[] {
  const pages: number[] = []
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
}

// ==================== Tab Switching ====================

function switchTab(tabId: string) {
  activeTab.value = tabId
  if (!tabLoaded.value[tabId]) {
    tabLoaded.value[tabId] = true
    if (tabId === 'transactions') fetchTransactions(1)
    else if (tabId === 'withdrawals') fetchWithdrawals(1)
    else if (tabId === 'reports') fetchSummary()
  }
}

// ==================== Transactions API ====================

async function fetchTransactions(page: number) {
  txLoading.value = true
  try {
    const params: Record<string, string | number> = {
      page,
      sort: txSort.value.field,
      direction: txSort.value.direction,
    }
    if (txFilters.value.type) params.type = txFilters.value.type
    if (txFilters.value.status) params.status = txFilters.value.status
    if (txFilters.value.date_from) params.date_from = txFilters.value.date_from
    if (txFilters.value.date_to) params.date_to = txFilters.value.date_to
    if (txFilters.value.player_search) params.player_search = txFilters.value.player_search
    if (txFilters.value.amount_min) {
      params.amount_min = Math.round(parseFloat(txFilters.value.amount_min) * 100)
    }
    if (txFilters.value.amount_max) {
      params.amount_max = Math.round(parseFloat(txFilters.value.amount_max) * 100)
    }

    const response = await apiClient.get('/admin/api/transactions', { params })
    transactions.value = response.data.data
    txMeta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.finance.toast_load_transactions_failed'))
  } finally {
    txLoading.value = false
  }
}

function toggleTxSort(field: string) {
  if (txSort.value.field === field) {
    txSort.value.direction = txSort.value.direction === 'asc' ? 'desc' : 'asc'
  } else {
    txSort.value.field = field
    txSort.value.direction = 'desc'
  }
  fetchTransactions(1)
}

async function handleExportCsv() {
  exportingCsv.value = true
  try {
    const params: Record<string, string | number> = {}
    if (txFilters.value.type) params.type = txFilters.value.type
    if (txFilters.value.status) params.status = txFilters.value.status
    if (txFilters.value.date_from) params.date_from = txFilters.value.date_from
    if (txFilters.value.date_to) params.date_to = txFilters.value.date_to
    if (txFilters.value.player_search) params.player_search = txFilters.value.player_search
    if (txFilters.value.amount_min) {
      params.amount_min = Math.round(parseFloat(txFilters.value.amount_min) * 100)
    }
    if (txFilters.value.amount_max) {
      params.amount_max = Math.round(parseFloat(txFilters.value.amount_max) * 100)
    }

    const response = await apiClient.get('/admin/api/finance/export', {
      params,
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'transactions-export.csv')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    toast.success(t('admin.finance.toast_export_success'))
  } catch {
    toast.error(t('admin.finance.toast_export_failed'))
  } finally {
    exportingCsv.value = false
  }
}

// ==================== Withdrawals API ====================

async function fetchWithdrawals(page: number) {
  wdLoading.value = true
  expandedId.value = null
  expandedDetail.value = null

  try {
    const params: Record<string, string | number> = { page, per_page: 15 }
    if (wdFilters.value.status) params.status = wdFilters.value.status
    if (wdFilters.value.date_from) params.date_from = wdFilters.value.date_from
    if (wdFilters.value.date_to) params.date_to = wdFilters.value.date_to
    if (wdFilters.value.search) params.search = wdFilters.value.search

    const response = await apiClient.get('/admin/api/withdrawals', { params })
    withdrawals.value = response.data.data
    wdMeta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }

    const currentIds = withdrawals.value.map((w) => w.id)
    selectedIds.value = selectedIds.value.filter((id) => currentIds.includes(id))
  } catch {
    toast.error(t('admin.finance.toast_load_withdrawals_failed'))
  } finally {
    wdLoading.value = false
  }
}

function toggleSelectAll() {
  if (allPendingSelected.value) {
    selectedIds.value = selectedIds.value.filter((id) => !pendingIds.value.includes(id))
  } else {
    const newIds = pendingIds.value.filter((id) => !selectedIds.value.includes(id))
    selectedIds.value = [...selectedIds.value, ...newIds]
  }
}

function toggleSelect(id: number) {
  const idx = selectedIds.value.indexOf(id)
  if (idx === -1) {
    selectedIds.value.push(id)
  } else {
    selectedIds.value.splice(idx, 1)
  }
}

function toggleExpand(id: number) {
  if (expandedId.value === id) {
    expandedId.value = null
    expandedDetail.value = null
  } else {
    expandedId.value = id
    expandedDetail.value = null
    fetchDetail(id)
  }
}

async function fetchDetail(id: number) {
  expandedLoading.value = true
  try {
    const response = await apiClient.get(`/admin/api/withdrawals/${id}`)
    expandedDetail.value = response.data
  } catch {
    toast.error(t('admin.finance.toast_load_detail_failed'))
    expandedId.value = null
  } finally {
    expandedLoading.value = false
  }
}

async function handleApprove(id: number) {
  approvingId.value = id
  try {
    await apiClient.post(`/admin/api/withdrawals/${id}/approve`)
    toast.success(t('admin.finance.toast_approved'))
    await fetchWithdrawals(wdMeta.value.current_page)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    toast.error(err.response?.data?.message || t('admin.finance.toast_approve_failed'))
  } finally {
    approvingId.value = null
  }
}

function openRejectModal(tx: Withdrawal) {
  rejectModal.value = {
    visible: true,
    transactionId: tx.id,
    username: tx.player_username,
    amount: tx.amount,
    currency: tx.currency,
    reason: '',
    loading: false,
    error: '',
  }
}

function closeRejectModal() {
  rejectModal.value.visible = false
}

async function handleReject() {
  if (!rejectModal.value.reason.trim()) return

  rejectModal.value.loading = true
  rejectModal.value.error = ''

  try {
    await apiClient.post(`/admin/api/withdrawals/${rejectModal.value.transactionId}/reject`, {
      reason: rejectModal.value.reason.trim(),
    })
    toast.success(t('admin.finance.toast_rejected'))
    closeRejectModal()
    await fetchWithdrawals(wdMeta.value.current_page)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    rejectModal.value.error = err.response?.data?.message || t('admin.finance.toast_reject_failed')
  } finally {
    rejectModal.value.loading = false
  }
}

async function handleBatchApprove() {
  if (selectedIds.value.length === 0) return

  batchApproving.value = true
  try {
    const response = await apiClient.post('/admin/api/withdrawals/batch-approve', {
      ids: selectedIds.value,
    })
    const data = response.data
    if (data.failures?.length > 0) {
      toast.warning(t('admin.finance.toast_batch_partial', { approved: data.approved, failed: data.failures.length }))
    } else {
      toast.success(t('admin.finance.toast_batch_approved', { n: data.approved }))
    }
    selectedIds.value = []
    await fetchWithdrawals(wdMeta.value.current_page)
  } catch {
    toast.error(t('admin.finance.toast_batch_failed'))
  } finally {
    batchApproving.value = false
  }
}

// ==================== Reports API ====================

function onReportPeriodChange() {
  if (reportPeriod.value !== 'custom') {
    fetchSummary()
  }
}

async function fetchSummary() {
  reportLoading.value = true
  try {
    const params: Record<string, string> = { period: reportPeriod.value }
    if (reportPeriod.value === 'custom') {
      if (reportDateFrom.value) params.date_from = reportDateFrom.value
      if (reportDateTo.value) params.date_to = reportDateTo.value
    }

    const response = await apiClient.get('/admin/api/finance/summary', { params })
    summary.value = response.data
  } catch {
    toast.error(t('admin.finance.toast_load_summary_failed'))
  } finally {
    reportLoading.value = false
  }
}

// ==================== Init ====================

onMounted(() => {
  tabLoaded.value.transactions = true
  fetchTransactions(1)
})
</script>

<style scoped>
.finance-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.tab-btn {
  padding: 12px 20px;
  background: none;
  color: var(--text-secondary, #b1bad3);
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
}

.tab-btn:hover {
  color: var(--text-primary, #fff);
}

.tab-btn.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}

/* Section */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.section-actions {
  display: flex;
  gap: 8px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary, #fff);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: var(--color-danger, #ed4245);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  box-shadow: 0 0 24px rgba(237, 66, 69, 0.35);
  transform: translateY(-1px);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input[type="date"] {
  width: 150px;
  color-scheme: dark;
}

.filter-amount {
  width: 100px;
}

.search-wrap {
  display: flex;
  gap: 0;
}

.search-input {
  border-radius: var(--radius-md, 8px) 0 0 var(--radius-md, 8px);
  width: 200px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: none;
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: color 150ms ease;
}

.btn-search:hover {
  color: var(--accent-primary, #00e701);
}

/* Stats Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.stat-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.stat-positive {
  color: var(--color-success, #00e701);
}

.stat-negative {
  color: var(--color-danger, #ed4245);
}

/* Breakdown */
.breakdown-section {
  margin-bottom: 24px;
}

.breakdown-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 12px;
}

.currency-label {
  font-weight: 600;
  color: var(--text-primary, #fff);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  margin-bottom: 16px;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
}

.th-sortable:hover {
  color: var(--text-primary, #fff);
}

.sort-indicator {
  margin-left: 4px;
  color: var(--accent-primary, #00e701);
}

.th-checkbox {
  width: 40px;
  text-align: center;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.td-checkbox {
  width: 40px;
  text-align: center;
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

/* Player Cell */
.player-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.player-username {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 13px;
}

.player-email {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Amount */
.amount-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
}

.amount-positive {
  color: var(--color-success, #00e701);
}

.amount-negative {
  color: var(--color-danger, #ed4245);
}

.amount-currency {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-left: 4px;
}

/* Method Badge */
.method-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 215, 0, 0.1);
  color: var(--text-secondary, #b1bad3);
  border-radius: var(--radius-full, 9999px);
  font-size: 12px;
}

/* Destination */
.destination-text {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge-completed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-failed {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-cancelled {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-type-deposit {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-type-withdrawal {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-type-bet {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.badge-type-win {
  background: rgba(255, 215, 0, 0.15);
  color: #ffd700;
}

.badge-type-bonus {
  background: rgba(155, 89, 182, 0.15);
  color: #9b59b6;
}

.badge-type-adjustment {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-kyc-approved {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-kyc-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-kyc-none,
.badge-kyc-rejected {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.td-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Withdrawal Row */
.withdrawal-row {
  cursor: pointer;
}

.withdrawal-row.expanded {
  background: #253d4f;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-approve:hover:not(:disabled) {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

.btn-approve:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-reject:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.3);
}

/* Expanded Row */
.expanded-row td {
  background: rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 0;
}

.expanded-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
}

.expanded-content {
  padding: 16px 20px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.detail-full-address {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-md, 8px);
  font-size: 12px;
}

.detail-full-address code {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  word-break: break-all;
}

.detail-notes {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
}

/* Empty Row */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 1.5px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Checkbox */
input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--accent-primary, #00e701);
  cursor: pointer;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 8px;
}

.modal-subtitle {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin: 0 0 16px;
  line-height: 1.5;
}

.modal-field {
  margin-bottom: 16px;
}

.modal-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.modal-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.modal-error {
  padding: 8px 12px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  color: var(--color-danger, #ed4245);
  font-size: 13px;
  margin-bottom: 16px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Responsive */
@media (max-width: 1024px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .detail-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .finance-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .stat-grid {
    grid-template-columns: 1fr;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}

/* =========================================================
   RTL overrides
   ========================================================= */
[dir='rtl'] th {
  text-align: right;
}

/* Monetary values always stay LTR */
[dir='rtl'] .amount-value,
[dir='rtl'] .stat-value {
  direction: ltr;
  unicode-bidi: embed;
  display: inline-block;
}

/* Sort indicator: flip margin */
[dir='rtl'] .sort-indicator {
  margin-left: 0;
  margin-right: 4px;
}

/* Modal text alignment */
[dir='rtl'] .modal-title,
[dir='rtl'] .detail-label {
  text-align: right;
}
</style>
